import React from "react";
import {DynamicFormsSetupEditorData} from "./DynamicFormsSetupEditorData";
import {FormSetup} from "../models/definitions/FormSetup";
import {
	Button,
	Checkbox,
	Stack,
} from "@mui/material";
import {questionFrontEndNames} from "../models/definitions/QuestionFrontEndName";
import {questionClientNames} from "../models/definitions/QuestionClientName";
import {questionValueTypes} from "../models/definitions/QuestionValueType";
import {questionQueryTypes} from "../models/definitions/QuestionQueryType";
import {questionUnits} from "../models/definitions/QuestionUnit";
import {Delete} from "@mui/icons-material";
import {QuestionSetup} from "../models/definitions/QuestionSetup";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {graphQLServices} from "../../../services/GraphQLServices";
import {DELETE_QUESTION_SETUP, PUT_QUESTION_SETUP} from "../questions/Queries";
import {newDefinition} from "../questions/DynamicFormsQuestionDetails";
import {DELETE_FORM_SETUP, PUT_FORM_SETUP} from "../Queries";
import {
	DropdownOption, jsonField, JsonListField,
	optionsField,
	readonlyField,
	Row,
	simpleField,
	StringListField,
	ValueOptionsField
} from "./Fields";
import {translateFunction} from "../../../localization/TranslationService";
import {GlobalState} from "../../../global/GlobalState";
import {dynamicFormsDefaultTargetStage} from "../DynamicFormsOverviewPage";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";

const translate = translateFunction("SureBasePortalDynamicForms");

export interface DynamicFormsEditorProps<TSetup extends FormSetup | QuestionSetup> {
	readonly data: DynamicFormsSetupEditorData<TSetup>;
	readonly update: (data: DynamicFormsSetupEditorData<TSetup>) => void;
	readonly names?: string[];
	readonly disabled?: boolean;
}

export function DynamicFormsFormEditor(props: DynamicFormsEditorProps<FormSetup>) {
	const data = props.data;
	
	function updateState(field: string, value: any) {
		const newSetup = {...data.setup, [field]: value};
		// if the frontEndName or clientId change, the formId has to be updated accordingly
		const formId = `${newSetup.formName}:${newSetup.frontEndName ?? "*"}:${newSetup.clientId ?? "*"}`;
		const withId = {...newSetup, formId};
		props.update(data.copy({
			setup: withId
		}));
	}
	
	function updateMetadata(newData: {
		isDeleted?: boolean;
		isNew?: boolean;
	}) {
		props.update(data.copy(newData));
	}
	
	const disabled = props.disabled;
	
	return <Stack>
		{formsActionsRow(data, updateMetadata)}

		{data.isDeleted ? [] : [
			readonlyField(data, "formName"),
			data.isNew
				? optionsField(data, "frontEndName", updateState, frontEndOptions, {disabled})
				: readonlyField(data, "frontEndName"),
			data.isNew
				? optionsField(data, "clientId", updateState, clientNameOptions, {disabled})
				: readonlyField(data, "clientId"),
			StringListField(data, "remoteFormsNames", updateState, {disabled}), // todo convert to dropdowns
			JsonListField(data, "questions", updateState, {disabled}),

			jsonField(data, "layout", updateState, {placeholder: "{ }", maxHeight: "1000px", disabled}),
		]}
	</Stack>;
}

// done add overrides
// done include extending questions
// done form/question rendering for non-standard questions (dropdown, autocomplete, date, number, email, etc)
// todo make rendering consistent and feature complete
// todo implement all expressions
// done implement important expressions
// done add preview reference inputs
// wont-do add preview query output
// todo add validator evaluation
// done add form editor
// done add deploy ui
// todo evaluate editor performance (feels clunky)
// done fix Extends autocomplete auto-select
// todo add a button to add a frontend/client override
// todo warning when leaving page
// done fix order of diff in deploy ui to correctly show what has been added and removed
// todo make help button with link to wiki containing the data from spreadsheets and cheat sheet
export function DynamicFormsQuestionEditor(props: DynamicFormsEditorProps<QuestionSetup>) {
	const data = props.data;
	
	function updateState(field: string, value: any) {
		const newSetup = {...data.setup, [field]: value};
		// if the frontEndName or clientName change, the fullId has to be updated accordingly
		const fullId = `${newSetup.definition}:${newSetup.frontEndName ?? "*"}:${newSetup.clientName ?? "*"}`;
		const withId = {...newSetup, fullId};
		props.update(data.copy({
			setup: withId
		}));
	}
	
	function updateMetadata(newData: {
		isDeleted?: boolean;
		isNew?: boolean;
	}) {
		props.update(data.copy(newData));
	}
	
	const extendsOptions = props.names ? [...props.names] : [];
	const index = extendsOptions.indexOf(data.setup.definition);
	if (index >= 0) { // only splice array when item is found
		extendsOptions.splice(index, 1); // 2nd parameter means remove one item only
	}
	
	const disabled = props.disabled;
	
	return <Stack>
		{questionsActionsRow(data, updateMetadata, {disabled})}
		
		{data.isDeleted ? [] : [
			readonlyField(data, "definition"),
			data.isNew
				? optionsField(data, "frontEndName", updateState, frontEndOptions, {disabled})
				: readonlyField(data, "frontEndName"),
			data.isNew
				? optionsField(data, "clientName", updateState, clientNameOptions, {disabled})
				: readonlyField(data, "clientName"),
			simpleField(data, "extends", updateState, {options: extendsOptions, disabled}),
			StringListField(data, "parameters", updateState, {disabled}),
			simpleField(data, "label", updateState, {isWrapped: true, isMultiline: true, disabled}),
			simpleField(data, "subLabel", updateState, {isWrapped: true, isMultiline: true, disabled}),
			simpleField(data, "description", updateState, {isWrapped: true, isMultiline: true, disabled}),
			simpleField(data, "placeholder", updateState, {isWrapped: true, isMultiline: true, disabled}),
			simpleField(data, "inputMask", updateState, {isWrapped: true, isMultiline: true, disabled}),
			optionsField(data, "valueType", updateState, valueTypeOptions, {isWrapped: true, disabled}),
			optionsField(data, "queryType", updateState, queryTypeOptions, {isWrapped: true, disabled}),
			ValueOptionsField(data, "valueOptions", updateState, {disabled}),
			optionsField(data, "unit", updateState, unitOptions, {isWrapped: true, disabled}),
			simpleField(data, "defaultValue", updateState, {isWrapped: true, disabled}),
			StringListField(data, "mapping", updateState, {isWrapped: true, disabled}),
			simpleField(data, "missingRequiredValueErrorMessage", updateState, {isWrapped: true, disabled}),
			jsonField(data, "labelExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "subLabelExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "defaultValueExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "valueMapping", updateState, {isWrapped: true, disabled}),
			jsonField(data, "visibleExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "enabledExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "editableExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "requiredExpression", updateState, {isWrapped: true, disabled}),
			jsonField(data, "validators", updateState, {placeholder: "[ ]", disabled}),
			
			// todo make `defaultPositioning` field type-safe by providing fields for each property
			jsonField(data, "defaultPositioning", updateState, {isWrapped: true, disabled}),

			// todo make `custom` field type-safe by providing fields for each property
			jsonField(data, "custom", updateState, {disabled}),

			jsonField(data, "rendererProperties", updateState, {disabled}),
		]}
	</Stack>;
}

const frontEndOptions: DropdownOption[] = optionsFromString(questionFrontEndNames);
const clientNameOptions: DropdownOption[] = optionsFromString(questionClientNames);
const valueTypeOptions: DropdownOption[] = optionsFromString(questionValueTypes);
const queryTypeOptions: DropdownOption[] = optionsFromString(questionQueryTypes);
const unitOptions: DropdownOption[] = optionsFromString(questionUnits);

function optionsFromString(options: readonly string[]): DropdownOption[] {
	return options.map(it => ({key: it, display: it}));
}

function questionsActionsRow(
	data: DynamicFormsSetupEditorData<QuestionSetup>,
	updateData: (newData: { isDeleted?: boolean; isNew?: boolean }) => void,
	{disabled = false}: { disabled?: boolean } = {}
) {
	return <Row key={"editorActions"}>
		<Checkbox style={{visibility: "hidden"}}/>
		<Stack flexGrow={1} direction={"row-reverse"} justifyContent={"space-between"} alignItems={"flex-start"}>
			<ProcessButton
				defaultText={translate("Save")}
				inProgressText={translate("Saving")}
				completedText={translate("Saved")}
				resetKeys={[data.setup, data.isDeleted]}
				variant={"outlined"}
				onClick={() => applyQuestionSetupChanges(data, updateData)}
				buttonProps={{
					disabled: disabled
				}}
			/>
			
			{data.isNew
				? undefined
				: <Button
					key={"delete"}
					startIcon={<Delete/>}
					variant={"outlined"}
					onClick={() => updateData({isDeleted: !data.isDeleted})}
					disabled={disabled}
				>{data.isDeleted ? translate("Undo") : translate("Delete")}</Button>
			}
		</Stack>
	</Row>;
}

async function applyQuestionSetupChanges(data: DynamicFormsSetupEditorData<QuestionSetup>, updateData: (newData: { isNew?: boolean }) => void) {
	const targetStage = GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage);
	targetStageSafetyCheck(targetStage);
	
	if (data.isDeleted) {
		await deleteQuestionSetup(data.setup, targetStage);
	} else {
		await saveQuestionSetup(data.setup, updateData, targetStage);
	}
}

async function deleteQuestionSetup(setup: QuestionSetup, targetStage: string) {
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: DELETE_QUESTION_SETUP,
		variables: {
			stage: targetStage,
			questionDefinition: setup.definition,
			frontEndName: setup.frontEndName,
			clientName: setup.clientName,
		}
	});
}

async function saveQuestionSetup(setup: QuestionSetup, updateData: (newData: { isNew?: boolean }) => void, targetStage: string) {
	const questionSetup: any = {...setup};
	delete questionSetup.fullId;
	delete questionSetup.versionId;
	delete questionSetup.id;
	delete questionSetup.version;
	delete questionSetup.creationDateTime;
	delete questionSetup.lastModifiedDateTime;
	delete questionSetup.lastModifiedBy;
	
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: PUT_QUESTION_SETUP,
		variables: {
			stage: targetStage,
			questionSetup: questionSetup
		}
	});
	
	updateData({isNew: false});
	newDefinition.set(null);
}

function formsActionsRow(data: DynamicFormsSetupEditorData<FormSetup>, updateData: (newData: { isDeleted?: boolean; isNew?: boolean }) => void) {
	return <Row key={"editorActions"}>
		<Checkbox style={{visibility: "hidden"}}/>
		<Stack flexGrow={1} direction={"row-reverse"} justifyContent={"space-between"} alignItems={"flex-start"}>
			<ProcessButton
				defaultText={translate("Save")}
				inProgressText={translate("Saving")}
				completedText={translate("Saved")}
				resetKeys={[data.setup, data.isDeleted]}
				variant={"outlined"}
				onClick={() => applyFormSetupChanges(data, updateData)}/>
			
			{data.isNew
				? undefined
				: <Button
					key={"delete"}
					startIcon={<Delete/>}
					variant={"outlined"}
					onClick={() => updateData({isDeleted: !data.isDeleted})}
				>{data.isDeleted ? translate("Undo") : translate("Delete")}</Button>
			}
		</Stack>
	</Row>;
}

async function applyFormSetupChanges(data: DynamicFormsSetupEditorData<FormSetup>, updateData: (newData: { isNew?: boolean }) => void) {
	const targetStage = GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage);
	targetStageSafetyCheck(targetStage);
	
	if (data.isDeleted) {
		await deleteFormSetup(data.setup, targetStage);
	} else {
		await saveFormSetup(data.setup, updateData, targetStage);
	}
}

async function deleteFormSetup(setup: FormSetup, targetStage: string) {
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: DELETE_FORM_SETUP,
		variables: {
			stage: targetStage,
			formName: setup.formName,
			frontEndName: setup.frontEndName,
			clientName: setup.clientId,
		}
	});
}

async function saveFormSetup(setup: FormSetup, updateData: (newData: { isNew?: boolean }) => void, targetStage: string) {
	const formSetup: any = {...setup};
	delete formSetup.formId;
	delete formSetup.versionId;
	delete formSetup.id;
	delete formSetup.version;
	delete formSetup.creationDateTime;
	delete formSetup.lastModifiedDateTime;
	delete formSetup.lastModifiedBy;
	
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: PUT_FORM_SETUP,
		variables: {
			stage: targetStage,
			formSetup: formSetup
		}
	});
	
	updateData({isNew: false});
	newDefinition.set(null);
}
